import type { IEnrollmentRepository } from "../repositories/EnrollmentRepository";
import type {
  EnrollmentPlan,
  SiteEnrollmentRow,
  MonthlyForecast,
  UpdateStudyTargetRequest,
  UpdateSiteTargetRequest,
  CreateForecastRowRequest,
  UpdateForecastRowRequest,
  GenerateForecastRequest,
} from "@/pages/StudyManagement/components/enrollment/types";
// ─── Interface ────────────────────────────────────────────────────────────────
export interface IEnrollmentUseCase {
  getEnrollmentPlan(studyId: string): Promise<EnrollmentPlan>;
  updateStudyTarget(
    studyId: string,
    studyTarget: number,
  ): Promise<{
    studyTarget: number;
  }>;
  getSiteEnrollments(studyId: string): Promise<SiteEnrollmentRow[]>;
  updateSiteTarget(
    studyId: string,
    siteId: string,
    targetEnrollment: number,
  ): Promise<SiteEnrollmentRow>;
  getForecast(studyId: string): Promise<MonthlyForecast[]>;
  createForecastRow(
    studyId: string,
    req: CreateForecastRowRequest,
  ): Promise<MonthlyForecast>;
  updateForecastRow(
    studyId: string,
    rowId: string,
    plannedEnrollment: number,
  ): Promise<MonthlyForecast>;
  deleteForecastRow(studyId: string, rowId: string): Promise<void>;
  generateForecast(
    studyId: string,
    req: GenerateForecastRequest,
  ): Promise<MonthlyForecast[]>;
}
// ─── Implementation ───────────────────────────────────────────────────────────
export class EnrollmentUseCase implements IEnrollmentUseCase {
  constructor(private readonly enrollmentRepository: IEnrollmentRepository) {}
  private extractErrorMessage(error: unknown): string {
    if (error instanceof Error) {
      return error.message;
    }
    if (
      error &&
      typeof error === "object" &&
      "message" in error &&
      typeof (error as Record<string, unknown>).message === "string"
    ) {
      return (error as Record<string, unknown>).message as string;
    }
    return "An unknown error occurred";
  }
  async getEnrollmentPlan(studyId: string): Promise<EnrollmentPlan> {
    try {
      return await this.enrollmentRepository.getEnrollmentPlan(studyId);
    } catch (error) {
      throw new Error(this.extractErrorMessage(error));
    }
  }
  async updateStudyTarget(
    studyId: string,
    studyTarget: number,
  ): Promise<{
    studyTarget: number;
  }> {
    if (studyTarget <= 0) {
      throw new Error("Study target must be greater than 0");
    }
    const req: UpdateStudyTargetRequest = { studyTarget };
    try {
      return await this.enrollmentRepository.updateStudyTarget(studyId, req);
    } catch (error) {
      throw new Error(this.extractErrorMessage(error));
    }
  }
  async getSiteEnrollments(studyId: string): Promise<SiteEnrollmentRow[]> {
    try {
      return await this.enrollmentRepository.getSiteEnrollments(studyId);
    } catch (error) {
      throw new Error(this.extractErrorMessage(error));
    }
  }
  async updateSiteTarget(
    studyId: string,
    siteId: string,
    targetEnrollment: number,
  ): Promise<SiteEnrollmentRow> {
    if (targetEnrollment < 0) {
      throw new Error("Site target cannot be negative");
    }
    const req: UpdateSiteTargetRequest = { targetEnrollment };
    try {
      return await this.enrollmentRepository.updateSiteTarget(
        studyId,
        siteId,
        req,
      );
    } catch (error) {
      throw new Error(this.extractErrorMessage(error));
    }
  }
  async getForecast(studyId: string): Promise<MonthlyForecast[]> {
    try {
      return await this.enrollmentRepository.getForecast(studyId);
    } catch (error) {
      throw new Error(this.extractErrorMessage(error));
    }
  }
  async createForecastRow(
    studyId: string,
    req: CreateForecastRowRequest,
  ): Promise<MonthlyForecast> {
    if (!req.month) {
      throw new Error("Month is required");
    }
    if (req.plannedEnrollment < 0) {
      throw new Error("Planned enrollment cannot be negative");
    }
    try {
      return await this.enrollmentRepository.createForecastRow(studyId, req);
    } catch (error) {
      throw new Error(this.extractErrorMessage(error));
    }
  }
  async updateForecastRow(
    studyId: string,
    rowId: string,
    plannedEnrollment: number,
  ): Promise<MonthlyForecast> {
    if (plannedEnrollment < 0) {
      throw new Error("Planned enrollment cannot be negative");
    }
    const req: UpdateForecastRowRequest = { plannedEnrollment };
    try {
      return await this.enrollmentRepository.updateForecastRow(
        studyId,
        rowId,
        req,
      );
    } catch (error) {
      throw new Error(this.extractErrorMessage(error));
    }
  }
  async deleteForecastRow(studyId: string, rowId: string): Promise<void> {
    try {
      await this.enrollmentRepository.deleteForecastRow(studyId, rowId);
    } catch (error) {
      throw new Error(this.extractErrorMessage(error));
    }
  }
  async generateForecast(
    studyId: string,
    req: GenerateForecastRequest,
  ): Promise<MonthlyForecast[]> {
    try {
      return await this.enrollmentRepository.generateForecast(studyId, req);
    } catch (error) {
      throw new Error(this.extractErrorMessage(error));
    }
  }
}
